import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  broadcastLogout,
  broadcastLogin,
  subscribeToAuthSync,
} from "@oneapp/data";
import { AnalyticsEvents } from "@repo/analytics";
import { analyticsManager } from "../lib/setupAnalytics";
import { clearAuthAnalyticsIdentity } from "../lib/authAnalytics";
import { queryClient } from "../lib/queryClient";
import { clearAinaMentorIconNudgeSession } from "../lib/aina-intro/ainaMentorIconNudgeSession";
import { clearAinaIntroDismissed } from "../lib/aina-intro/storage";
import { primeAinaIntroVideoFromUserGesture } from "../lib/aina-intro/primer";
import { clearExamSessionStorage } from "@repo/tests-web/cleanup";
import {
  resetClarityCustomUserIdCache,
  syncClarityCustomUserId,
} from "../lib/clarityIdentity";
import {
  getPrimaryEnrolledProgram,
  mergeEnrolledProgramsWithDefaultSelection,
  readSelfLearnSelectedProgramIdFromStorage,
} from "@repo/self-learn-web";
import {
  clearSelfLearnSelectedProgramStorage,
  syncSelfLearnSelectedProgramFromEnrollment,
} from "../lib/syncSelfLearnSelectedProgram";

export interface User {
  id: string;
  name: string;
  role: "student" | "guest";
}
export interface ProfileItem {
  uam_id?: number;
  admission_id?: string;
  crn_id?: string;
  branch_id?: string;
  tenant_id?: string;
  vertical_id?: number;
  is_default_profile?: boolean;
  is_temp_password?: boolean;
  tenant_code?: string;
  exams?: { examId: number; name: string }[];
  [key: string]: unknown;
}

export interface UserAccountDetailsRole {
  role_id: number;
  role_name: string;
  created_time: number;
  role_code: string;
  [key: string]: unknown;
}

export interface UserAccountDetailsUserType {
  name: string;
  user_type_id: number;
  created_time: number;
  active: boolean;
  [key: string]: unknown;
}

export interface UserAccountDetailsGrade {
  gradeId: number;
  name: string;
}

export interface UserAccountDetails {
  academic_section?: string | null;
  activation_test?: unknown;
  board?: string | null;
  campus?: string | null;
  country?: string | null;
  created_at?: string;
  description?: string | null;
  designation?: string | null;
  dob?: string | null;
  email?: string;
  first_name?: string;
  gender?: string | null;
  grade?: UserAccountDetailsGrade | null;
  guardian_name?: string | null;
  guardian_phone?: string | null;
  is_active?: boolean;
  is_pass_set?: boolean;
  is_walkthrough?: unknown;
  isd_code?: string;
  last_logged_in_time?: string;
  last_name?: string;
  phone?: string;
  profile_photo?: string | null;
  remaining_phone_change_attempts?: number;
  role?: UserAccountDetailsRole;
  updated_at?: string;
  user_deactivation?: boolean;
  user_type?: UserAccountDetailsUserType;
  whatsapp_consent?: boolean;
  [key: string]: unknown;
}

export type UnifiedAccountDetails = {
  unified_account_id?: string;
  profiles?: ProfileItem[];
  user_account_details?: UserAccountDetails;
  [key: string]: unknown;
} | null;

export interface AccountsData {
  access_token: string;
  refresh_token: string;
  is_user_existing: boolean;
  unified_accounts: {
    unified_account_id: string;
    first_name: string;
    last_name: string;
    grade?: number;
    user_account_photo?: string | null;
  }[];
}

export interface ProfilesData {
  access_token?: string;
  refresh_token?: string;
  response?: string;
  unified_account_details: {
    unified_account_id?: string;
    profiles?: ProfileItem[];
    user_account_details?: UserAccountDetails;
    [key: string]: unknown;
  };
}

export interface EnrolledProgram {
  program_id?: string;
  program_name?: string;
  gradeId?: number;
  examId?: number;
  gradeName?: string;
  examName?: string;
  batch?: string | null;
  tenant_id?: string;
  subtenant_id?: number;
  vertical_id?: number;
  subjects?: unknown[];
  mdsExamName?: string;
  mdsExamId?: number;
  mdsGradeName?: string;
  mdsGradeId?: number;
  duration_in_weeks?: number;
  mdsOfferingName?: string;
  mdsOfferingId?: number;
  programOfferingIds?: number[];
  isDefault?: boolean;
  [key: string]: unknown;
}

export interface ModuleDataDto {
  accessToken: string | null;
  refreshToken: string | null;
  accountsData: AccountsData | null;
  profilesData: ProfilesData | null;
  enrolledPrograms: EnrolledProgram[] | null;
  setSessionData: (tenantId: string | null, subtenantId: number | null) => void;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  /** In-memory only; not persisted. Use for OTP flow before full login. */
  transientAccessToken: string | null;
  transientRefreshToken: string | null;
  user: User | null;
  unifiedAccountDetails: UnifiedAccountDetails;
  /** Login-with-OTP response. Persisted in localStorage. */
  accountsData: AccountsData | null;
  /** Profiles API or register response. Persisted in localStorage. */
  profilesData: ProfilesData | null;
  /** Enrolled programs array. Persisted in localStorage. */
  enrolledPrograms: EnrolledProgram[] | null;
  /** In-memory flag to prevent logout during profile sync. */
  isSyncing: boolean;
  loginMethod: "phone_otp" | "username_password" | "admission_id" | null;
  login: (
    token: string,
    user: User,
    refreshToken?: string,
    broadcast?: boolean,
    options?: { skipAinaIntroPrime?: boolean },
  ) => void;
  /** `broadcast` false = session cleared by API (no logout analytics). */
  logout: (broadcast?: boolean) => void;
  setTokens: (token: string, refreshToken?: string) => void;
  setTransientTokens: (token: string, refreshToken?: string) => void;
  clearTransientTokens: () => void;
  setUnifiedAccountDetails: (details: UnifiedAccountDetails) => void;
  setAccountsData: (data: AccountsData | null) => void;
  setProfilesData: (data: ProfilesData | null) => void;
  setEnrolledPrograms: (programs: EnrolledProgram[] | null) => void;
  setIsSyncing: (isSyncing: boolean) => void;
  setLoginMethod: (
    method: "phone_otp" | "username_password" | "admission_id" | null,
  ) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      transientAccessToken: null,
      transientRefreshToken: null,
      user: null,
      unifiedAccountDetails: null,
      accountsData: null,
      profilesData: null,
      enrolledPrograms: null,
      isSyncing: false,
      loginMethod: null,

      login: (token, user, refreshToken, broadcast = true, options) => {
        set({ token, user, refreshToken: refreshToken ?? null });
        if (!options?.skipAinaIntroPrime) {
          primeAinaIntroVideoFromUserGesture();
        }
        if (broadcast) {
          broadcastLogin({
            accessToken: token,
            refreshToken: refreshToken,
            user,
          });
        }
      },
      logout: (broadcast = true) => {
        if (broadcast) {
          analyticsManager.track(AnalyticsEvents.LOGOUT, 1, {});
        }
        clearAuthAnalyticsIdentity();
        resetClarityCustomUserIdCache();
        clearAinaMentorIconNudgeSession();
        clearAinaIntroDismissed();
        set({
          token: null,
          refreshToken: null,
          transientAccessToken: null,
          transientRefreshToken: null,
          user: null,
          unifiedAccountDetails: null,
          accountsData: null,
          profilesData: null,
          enrolledPrograms: null,
          loginMethod: null,
        });
        localStorage.removeItem("cart-items");
        localStorage.removeItem("web_deeplink_url");
        localStorage.removeItem("sca_is_phone_verified");
        clearExamSessionStorage();
        clearSelfLearnSelectedProgramStorage();
        queryClient.clear();
        if (broadcast) {
          broadcastLogout();
        }
      },
      setTokens: (token, refreshToken) => {
        set({ token, refreshToken: refreshToken ?? null });
      },
      setTransientTokens: (token, refreshToken) =>
        set({
          transientAccessToken: token,
          transientRefreshToken: refreshToken ?? null,
        }),
      clearTransientTokens: () =>
        set({ transientAccessToken: null, transientRefreshToken: null }),
      setUnifiedAccountDetails: (unifiedAccountDetails) =>
        set({ unifiedAccountDetails }),
      setAccountsData: (accountsData) => set({ accountsData }),
      setProfilesData: (profilesData) => {
        set({ profilesData });
        localStorage.setItem(
          "user_details_from_server",
          JSON.stringify(
            profilesData?.unified_account_details?.user_account_details,
          ),
        );
        syncClarityCustomUserId(profilesData);
      },
      setEnrolledPrograms: (programs) => {
        const enrolledPrograms =
          programs && Array.isArray(programs)
            ? programs.map((p, i) => ({ ...p, isDefault: i === 0 }))
            : [];
        set({ enrolledPrograms });
        syncSelfLearnSelectedProgramFromEnrollment(enrolledPrograms);
      },
      setIsSyncing: (isSyncing) => set({ isSyncing }),
      setLoginMethod: (loginMethod) =>
        set((state) => {
          if (state.loginMethod && loginMethod !== null) return state;
          return { loginMethod };
        }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        accountsData: state.accountsData,
        profilesData: state.profilesData,
        enrolledPrograms: state.enrolledPrograms,
        loginMethod: state.loginMethod,
      }),
    },
  ),
);

const AUTH_STORAGE_KEY = "auth-storage";

if (typeof window !== "undefined") {
  useAuthStore.persist.onFinishHydration(() => {
    const { token, profilesData, enrolledPrograms } = useAuthStore.getState();
    if (token) {
      syncClarityCustomUserId(profilesData);
    }
    syncSelfLearnSelectedProgramFromEnrollment(enrolledPrograms);
  });
  subscribeToAuthSync({
    onLogin: (payload) => {
      // Cross-tab login: preserve accountsData, profilesData, enrolledPrograms
      // from localStorage (written by the tab that logged in) to avoid
      // overwriting them with null when this tab persists.
      let accountsData: AccountsData | null = null;
      let profilesData: ProfilesData | null = null;
      let enrolledPrograms: EnrolledProgram[] | null = null;
      let loginMethod:
        | "phone_otp"
        | "username_password"
        | "admission_id"
        | null = null;
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as {
            state?: {
              accountsData?: AccountsData | null;
              profilesData?: ProfilesData | null;
              enrolledPrograms?: EnrolledProgram[] | null;
              loginMethod?:
                | "phone_otp"
                | "username_password"
                | "admission_id"
                | null;
            };
          };
          const state = parsed?.state;
          if (state) {
            if (state.accountsData != null) accountsData = state.accountsData;
            if (state.profilesData != null) profilesData = state.profilesData;
            if (
              state.enrolledPrograms != null &&
              Array.isArray(state.enrolledPrograms)
            ) {
              enrolledPrograms = state.enrolledPrograms;
            }
            if (state.loginMethod !== undefined) {
              loginMethod = state.loginMethod;
            }
          }
        }
      } catch {
        // ignore parse errors
      }
      useAuthStore.setState({
        token: payload.accessToken,
        refreshToken: payload.refreshToken ?? null,
        user: payload.user as User | null,
        ...(accountsData !== null && { accountsData }),
        ...(profilesData !== null && { profilesData }),
        ...(enrolledPrograms !== null && { enrolledPrograms }),
        ...(loginMethod !== null && { loginMethod }),
      });
      syncClarityCustomUserId(
        profilesData ?? useAuthStore.getState().profilesData,
      );
    },
    onLogout: () => {
      clearAuthAnalyticsIdentity();
      clearAinaMentorIconNudgeSession();
      clearAinaIntroDismissed();
      clearExamSessionStorage();
      clearSelfLearnSelectedProgramStorage();
      useAuthStore.setState({
        token: null,
        refreshToken: null,
        transientAccessToken: null,
        transientRefreshToken: null,
        user: null,
        unifiedAccountDetails: null,
        accountsData: null,
        profilesData: null,
        enrolledPrograms: null,
        isSyncing: false,
        loginMethod: null,
      });
      localStorage.removeItem("user_details_from_server");
      localStorage.removeItem("sca_is_phone_verified");
    },
    onTokenUpdate: (payload) => {
      useAuthStore.setState({
        token: payload.accessToken,
        refreshToken: payload.refreshToken ?? null,
      });
    },
  });
}

export const useIsAuthenticated = () => useAuthStore((state) => !!state.token);
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthToken = () => useAuthStore((state) => state.token);
export const useRefreshToken = () =>
  useAuthStore((state) => state.refreshToken);
export const useTransientAccessToken = () =>
  useAuthStore((state) => state.transientAccessToken);
export const useUnifiedAccountDetails = () =>
  useAuthStore((state) => state.unifiedAccountDetails);
export const useAccountsData = () =>
  useAuthStore((state) => state.accountsData);
export const useProfilesData = () =>
  useAuthStore((state) => state.profilesData);
export const useMyPrograms = () =>
  useAuthStore((state) => state.enrolledPrograms);
export const useLoginMethod = () => useAuthStore((state) => state.loginMethod);

export function getCurrentProfileStudentGatewayId(
  profilesData: ProfilesData | null | undefined,
): string {
  const profiles = profilesData?.unified_account_details?.profiles;
  if (!Array.isArray(profiles) || profiles.length === 0) {
    return "";
  }
  const profile =
    profiles.find((p) => p.is_default_profile === true) ?? profiles[0];
  const uam = profile?.uam_id;
  if (uam == null) {
    return "";
  }
  return String(uam);
}

const MY_PROGRAMS_QUERY_KEY_PREFIX = "/students/my-programs";

function sameProgramOrder(
  a: EnrolledProgram[] | null | undefined,
  b: EnrolledProgram[],
): boolean {
  if (!Array.isArray(a) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.program_id !== b[i]?.program_id) return false;
  }
  return true;
}

queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated") return;
  if (event.query.state.status !== "success") return;

  const key = event.query.queryKey;
  if (!Array.isArray(key) || key[0] !== MY_PROGRAMS_QUERY_KEY_PREFIX) return;

  const authState = useAuthStore.getState();
  if (!authState.token) return;

  const raw = event.query.state.data as
    | { data?: { programs?: unknown } }
    | { programs?: unknown }
    | unknown
    | null
    | undefined;
  const unknownList = Array.isArray(raw)
    ? raw
    : ((raw as { data?: { programs?: unknown } })?.data?.programs ??
      (raw as { programs?: unknown })?.programs ??
      null);
  if (!Array.isArray(unknownList) || unknownList.length === 0) return;

  const list = unknownList as EnrolledProgram[];

  const storedId = readSelfLearnSelectedProgramIdFromStorage();
  const preferred =
    (storedId ? list.find((p) => p.program_id === storedId) : undefined) ??
    getPrimaryEnrolledProgram(list);
  if (!preferred?.program_id) return;

  const nextPrograms = mergeEnrolledProgramsWithDefaultSelection(
    list,
    preferred as EnrolledProgram & { program_id: string },
  );

  if (!sameProgramOrder(authState.enrolledPrograms, nextPrograms)) {
    authState.setEnrolledPrograms(nextPrograms);
  }
});

if (typeof window !== "undefined") {
  useAuthStore.subscribe((state, prevState) => {
    if (state.enrolledPrograms === prevState.enrolledPrograms) {
      return;
    }
    syncSelfLearnSelectedProgramFromEnrollment(state.enrolledPrograms);
  });
}

export const useAuthActions = () => {
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const setTokens = useAuthStore((state) => state.setTokens);
  const setTransientTokens = useAuthStore((state) => state.setTransientTokens);
  const clearTransientTokens = useAuthStore(
    (state) => state.clearTransientTokens,
  );
  const setUnifiedAccountDetails = useAuthStore(
    (state) => state.setUnifiedAccountDetails,
  );
  const setAccountsData = useAuthStore((state) => state.setAccountsData);
  const setProfilesData = useAuthStore((state) => state.setProfilesData);
  const setEnrolledPrograms = useAuthStore(
    (state) => state.setEnrolledPrograms,
  );
  const setIsSyncing = useAuthStore((state) => state.setIsSyncing);
  const setLoginMethod = useAuthStore((state) => state.setLoginMethod);
  return {
    login,
    logout,
    setTokens,
    setTransientTokens,
    clearTransientTokens,
    setUnifiedAccountDetails,
    setAccountsData,
    setProfilesData,
    setEnrolledPrograms,
    setIsSyncing,
    setLoginMethod,
  };
};
